import React from 'react';
import cn from 'classnames';
import { Avatar, Badge, Button } from '@odin-labs/components';
import { getInitialsForUser } from 'utils';
import { IdBadgeIcon, ShieldCheckIcon, TimesIcon } from 'components/icons';
import { SearchWorkerResult } from 'components/searchBox/types';

const classes = {
  container: cn('odin-flex odin-flex-col odin-space-y-2.5'),
  header: cn('odin-flex odin-items-center odin-space-x-2'),
  title: cn('odin-text-sm odin-font-medium odin-text-gray-900'),
  list: cn(
    'odin-flex odin-flex-wrap odin-gap-2',
    // three rows of items, then the list scrolls
    'odin-max-h-[7.75rem] odin-overflow-y-auto',
  ),
  item: cn(
    'odin-flex odin-items-center odin-space-x-2',
    'odin-h-9 odin-pl-1.5 odin-pr-1',
    'odin-border odin-border-gray-200 odin-rounded-lg odin-bg-odin-off-white',
  ),
  name: cn(
    'odin-text-sm odin-font-medium odin-text-gray-900 odin-whitespace-nowrap',
  ),
  info: cn('odin-flex odin-items-center odin-text-1.5xs odin-text-gray-900'),
  infoIcon: cn('odin-text-odin-primary odin-text-sm odin-mr-1'),
  empty: cn(
    'odin-flex odin-items-center odin-h-9 odin-px-3',
    'odin-border odin-border-dashed odin-border-gray-300 odin-rounded-lg',
    'odin-text-sm odin-text-gray-500',
  ),
};

export type AddedWorkersProps = {
  workers: SearchWorkerResult[];
  onRemove: (worker: SearchWorkerResult) => void;
};

/** The workers added since the add worker modal opened, each removable. */
export function AddedWorkers({
  workers,
  onRemove,
}: AddedWorkersProps): React.ReactElement {
  return (
    <section aria-label="Added this session" className={classes.container}>
      <div className={classes.header}>
        <h3 className={classes.title}>Added this session</h3>
        <Badge
          text={workers.length.toString()}
          color={workers.length ? 'purple' : 'gray'}
        />
      </div>
      {workers.length ? (
        <ul className={classes.list}>
          {workers.map((worker) => {
            const { id, firstName, lastName, imageUrl, quickCode, badgeId } =
              worker;
            const name = `${firstName} ${lastName}`;
            return (
              <li key={id} className={classes.item}>
                <Avatar
                  size="xs"
                  src={imageUrl}
                  placeholder={getInitialsForUser({
                    identity: { firstName, lastName },
                  })}
                  objectFit="cover"
                />
                <span className={classes.name}>{name}</span>
                <span className={classes.info}>
                  <ShieldCheckIcon
                    className={classes.infoIcon}
                    aria-hidden="true"
                  />
                  {quickCode}
                </span>
                {badgeId && (
                  <span className={classes.info}>
                    <IdBadgeIcon
                      className={classes.infoIcon}
                      aria-hidden="true"
                    />
                    {badgeId}
                  </span>
                )}
                <Button
                  theme="naked"
                  size="xs"
                  icon={TimesIcon}
                  aria-label={`Remove ${name}`}
                  onClick={(): void => onRemove(worker)}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <p className={classes.empty}>No workers added yet</p>
      )}
    </section>
  );
}
